/* eslint-disable no-console */
import React, { useContext, useEffect, useState, useMemo } from 'react';
// import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import '../../themes/quill-nile.css';
import { Context } from '../../store/Store';
import getI18nService from '../../i18n/i18n';
import { CustomTypography } from '../../common/CustomTypoGraphy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { countryCodes } from '../../common/metadata/DataEnums';

const i18n = getI18nService();

const Passcode = ({ setAccessCode, phoneState, reSendAccessCode, verificationStatus, setShowPasscodeForm }) => {
  const theme = useTheme();
  const [state, dispatch] = useContext(Context);

  const { api } = state;
  const portalData = api['get_portal'];
  const { smsConfig = {} } = portalData || {};
  const { expireTime, expireTimeUnit } = smsConfig;
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resendTime, setResendTime] = useState(60);
  const { verificationState, setVerificationState } = verificationStatus;
  const inputRefs = useMemo(
    () =>
      Array(6)
        .fill(0)
        .map(() => React.createRef()),
    []
  );

  // Countdown timer effect
  useEffect(() => {
    let interval = null;
    if (resendTime > 0) {
      interval = setInterval(() => {
        setResendTime(time => time - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTime]);

  // Add OTP handling functions
  const handleOtpChange = (index, value) => {
    if (isNaN(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto focus next input
    if (value !== '' && index < 5) {
      inputRefs[index + 1].current.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs[index - 1].current.focus();
    }
  };

  const startVerification = () => {
    setVerificationState('verifying');
    setAccessCode(otp.join(''));
  };

  const handleResendCode = () => {
    setResendTime(60);
    reSendAccessCode();
  };

  const getButtonContent = useMemo(() => {
    let icon = '';
    let text = '';
    switch (verificationState) {
      case 'verifying':
        icon = <CircularProgress size={12} color="inherit" />;
        text = 'Verifying...';
        break;
      case 'verified':
        icon = <CheckCircleIcon size={12} />;
        text = 'Success...';
        break;
      default:
        icon = '';
        text = 'Verify Now';
        break;
    }
    return (
      <Button
        variant="contained"
        color="primary"
        sx={{
          width: icon ? '340px' : '233px',
          height: '40px',
          textTransform: 'none',
        }}
        onClick={startVerification}
      >
        <Box display="flex" alignItems="center" width="100%" position="relative">
          {icon && (
            <Box position="absolute" left={2} top={4}>
              {icon}
            </Box>
          )}
          <CustomTypography sx={{ width: '100%', textAlign: 'center', fontWeight: 500, fontSize: '18px' }}>
            {text}
          </CustomTypography>
        </Box>
      </Button>
    );
  }, [verificationState, otp]);

  const formattedPhoneNumber = useMemo(() => {
    if (!phoneState || !phoneState.phoneNumber) return '';
    const { countryCode, phoneNumber } = phoneState;
    const cCode = countryCodes.find(c => c.code === countryCode);
    return `${cCode?.dialCode} ${phoneNumber}`;
  }, [phoneState]);

  const getTime = () => {
    const unit = expireTimeUnit.charAt(0).toUpperCase() + expireTimeUnit.slice(1).toLowerCase();
    return `${expireTime} ${unit}`;
  };

  return (
    <>
      <CustomTypography
        sx={{
          fontSize: {
            xs: '22px',
            sm: '24px',
          },
          textAlign: 'center',
          padding: '15px 0px 4px 0px',
        }}
      >
        {`${'Enter the 6-digit code we just sent to '} ${
          formattedPhoneNumber ? formattedPhoneNumber : 'your mobile number'
        }`}
      </CustomTypography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1,
          mt: 2,
          mb: 1,
        }}
      >
        {otp.map((digit, index) => (
          <TextField
            key={index}
            inputRef={inputRefs[index]}
            value={digit}
            onChange={e => handleOtpChange(index, e.target.value)}
            onKeyDown={e => handleKeyDown(index, e)}
            sx={{
              width: '53px',
              height: '56px',
              gap: 10,
              '& input': {
                textAlign: 'center',
                fontSize: '1.2rem',
                padding: '8px',
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.palette.text.dark,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.text.primary,
                },
              },
            }}
            inputProps={{
              maxLength: 1,
              type: 'tel',
            }}
            size="small"
          />
        ))}
      </Box>

      <Box>
        <CustomTypography
          sx={{
            fontSize: {
              xs: '14px',
              sm: '16px',
            },
            fontWeight: 400,
            color: 'text.hint',
            textAlign: {
              xs: 'left',
              sm: 'center',
            },
            mb: 2,
          }}
        >
          {resendTime > 0 ? (
            `Wait ${resendTime} seconds before requesting a new code.`
          ) : (
            <>
              {"Didn't receive it? "}
              <Link
                to="/#"
                sx={{ textDecoration: 'none', cursor: 'pointer' }}
                onClick={e => {
                  e.preventDefault();
                  handleResendCode();
                }}
              >
                Resend Code
              </Link>
            </>
          )}
        </CustomTypography>
      </Box>

      <Box display="flex" justifyContent="center" width="100%">
        {verificationState && getButtonContent}
      </Box>
      <Box display="flex" justifyContent="center" width="100%" mt={2}>
        <Link
          onClick={() => setShowPasscodeForm(false)}
          sx={{
            textDecoration: 'none',
            cursor: 'pointer',
            color: theme.palette.text.disabled,
            display: 'flex',
            alignItems: 'center',
            fontSize: '16px',
          }}
        >
          <ArrowBackIosIcon sx={{ fontSize: 16 }} />
          Back to Login
        </Link>
      </Box>
    </>
  );
};

Passcode.propTypes = {
  setAccessCode: PropTypes.func.isRequired,
  phoneState: PropTypes.object,
  reSendAccessCode: PropTypes.func,
  verificationStatus: PropTypes.object,
  setShowPasscodeForm: PropTypes.func,
};
Passcode.defaultProps = {
  phoneState: null,
  reSendAccessCode: () => {},
  verificationStatus: {},
  setShowPasscodeForm: () => {},
};

export default Passcode;
